"use client"

import {ChevronDown, Plus, Settings, SquareCheckBig} from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import {usePathname, useRouter} from "next/navigation";
import {CanGlobal} from "@/app/components/Can";
import {cn} from "@/lib/utils";
import {
  companyCreatePath,
  companyOnboardingPath,
  companyPath,
  companySettingsPath,
  integrationsPath,
  settingsWorkspaceDevelopmentPath,
  settingsWorkspaceGeneralPath,
  settingsWorkspaceIntegrationsPath,
  settingsWorkspaceMembersPath,
  settingsWorkspaceSetupRunsPath
} from "@/app/paths";
import {useOptionalCompanyId} from "@/hooks/use-company-id";
import {IconView} from "@/app/components/IconView";
import {useUserRoles} from "@/hooks/use-user-roles";
import {useOwnCompanies} from "@/hooks/queries/use-users";
import {gaId} from "@/lib/analytics/clickTracker";
import {matchPath} from "react-router";
import {usePreferredCompany} from "@/app/components/settings/usePreferredCompany";
import {useCompanyFeatureFlag} from "@/hooks/queries/use-feature-flags";

export function CompanySwitcher({showUrl, className}: { showUrl?: boolean, className?: string }) {
  const companyId = useOptionalCompanyId()
  const userCompanies = useOwnCompanies()
  const inOrg = userCompanies?.find(c => c.orgId !== undefined) !== undefined
  const router = useRouter()
  const pathname = usePathname()
  const {canCreateCompany} = useUserRoles()
  const {preferredCompany} = usePreferredCompany()
  const showOnboardingFlow = useCompanyFeatureFlag(ff => ff.wsOnboardingFlow)
  const activeCompany = companyId !== undefined
    ? userCompanies.find(c => c.id === companyId)
    : preferredCompany
  if (!canCreateCompany && userCompanies.length === 0) {
    return null
  }
  const logo = activeCompany?.logo

  const getTargetPath = (targetCompanyId: number) => {
    if (matchPath(companyOnboardingPath(":companyId"), pathname)) {
      return companyOnboardingPath(targetCompanyId)
    }
    if (matchPath(integrationsPath(":companyId"), pathname)) {
      return integrationsPath(targetCompanyId)
    }
    if (matchPath("/settings/workspace/:companyId/members", pathname)) {
      return settingsWorkspaceMembersPath(targetCompanyId)
    }
    if (matchPath("/settings/workspace/:companyId/integrations", pathname)) {
      return settingsWorkspaceIntegrationsPath(targetCompanyId)
    }
    if (matchPath("/settings/workspace/:companyId/general", pathname)) {
      return settingsWorkspaceGeneralPath(targetCompanyId)
    }
    if (matchPath("/settings/workspace/:companyId/development", pathname)) {
      return settingsWorkspaceDevelopmentPath(targetCompanyId)
    }
    if (matchPath("/settings/workspace/:companyId/setup-runs", pathname)) {
      return settingsWorkspaceSetupRunsPath(targetCompanyId)
    }
    if (pathname.startsWith("/settings")) {
      return companySettingsPath(targetCompanyId)
    }
    return companyPath(targetCompanyId)
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <div
          className={cn("flex justify-between items-center hover:bg-muted p-2 rounded cursor-pointer gap-2 group/company-switcher", className)}
          {...gaId("company_switcher")}
        >
          <div className="text-left text-sm">
            <div className="truncate font-semibold flex items-center gap-2">
              {logo && <IconView icon={logo} size="sm"/>}
              <div>{activeCompany ? activeCompany.name : "Select Workspace"}</div>
            </div>
            {activeCompany && showUrl && <div className="truncate text-xs">{activeCompany.website}</div>}
          </div>
          <ChevronDown className="ml-auto size-4 opacity-50 group-hover/company-switcher:opacity-100 transition-opacity" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        align="start"
        side={"right"}
        sideOffset={4}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Workspaces
        </DropdownMenuLabel>
        {userCompanies?.map((company) => (
          <DropdownMenuItem
            key={company.id}
            onClick={() => router.push(getTargetPath(company.id))}
            className="flex items-center gap-2 p-2"
          >
            {company.logo && <IconView icon={company.logo} size="sm"/>}{company.name}
          </DropdownMenuItem>
        ))}
        {activeCompany && <>
          <DropdownMenuSeparator/>
          <DropdownMenuItem className="gap-2 p-2" onClick={() => router.push(settingsWorkspaceGeneralPath(activeCompany.id))}>
            <div className="flex size-6 items-center justify-center rounded-md border bg-background">
              <Settings className="size-4"/>
            </div>
            <div className="font-medium text-muted-foreground">Workspace settings</div>
          </DropdownMenuItem>
          {showOnboardingFlow && (
            <DropdownMenuItem className="gap-2 p-2" onClick={() => router.push(companyOnboardingPath(activeCompany.id))}>
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <SquareCheckBig className="size-4"/>
              </div>
              <div className="font-medium text-muted-foreground">Set up step by step</div>
            </DropdownMenuItem>
          )}
        </>}
        <CanGlobal do="create" on="Company" blurForLimits={!inOrg}>
          <DropdownMenuSeparator/>
          <DropdownMenuItem className="gap-2 p-2" onClick={() => router.push(companyCreatePath())}>
            <div className="flex size-6 items-center justify-center rounded-md border bg-background">
              <Plus className="size-4"/>
            </div>
            <div className="font-medium text-muted-foreground">Create workspace</div>
          </DropdownMenuItem>
        </CanGlobal>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
